import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm, apiPostJson } from './client'

/**
 * Settings -> Auth Vault (Pass Safe), calea noua.
 *
 * Perechile vechi, din `application/controllers/settings/Pass_safe.php`:
 *
 *   getDeviceType        -> GET  /settings/pass_safe            (cadrul)
 *   getDeviceDetailsList -> GET  /settings/pass_safe/list       (lista)
 *   getDevicesByType     -> GET  /settings/pass_safe/devices/{type}
 *   searchDevices        -> POST /settings/pass_safe/search
 *   createPassword       -> POST /settings/pass_safe/passwords
 *   updatePassword       -> POST /settings/pass_safe/passwords/{id}
 *   deletePassword       -> POST /settings/pass_safe/passwords/{id}/remove
 *   showPassword         -> POST /settings/pass_safe/passwords/{id}/reveal
 *
 * PAROLA NU CIRCULA DECAT LA CERERE. Lista nu poarta nici textul cifrat (in
 * portal il purta, in campul `password` al fiecarui rand, desi nicio coloana
 * nu-l arata). Dezvaluirea e un POST separat, unul singur, care se scrie in
 * jurnal pe server.
 */

export type Option = { id: number; name: string }

/** O casuta ceruta de felul aparatului - `Services\PassSafe\CredentialProfile`. */
export type CredentialRole = {
  key: string
  label: string
  /** `false` = rolul e NUMAI o parola, fara cont (secretul de `enable`, PIN-ul). */
  user: boolean
  user_label: string
  hint: string
  required: boolean
}

export type PassSafeShell = {
  companyId: number
  filterDeviceTypes: Option[]
  deviceTypes: Option[]
  offices: Option[]
  companies: Option[]
  profiles: Record<string, CredentialRole[]>
  kindByType: Record<string, string>
  hasPasswords: boolean
  canWrite: boolean
}

export type PassSafeRow = {
  ID: number
  description: string
  username: string
  user: string
  device_type: string
  devices: string
  company: string
  DT_RowId: string
}

export type TablePage<T> = {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: T[]
}

export type DeviceHit = {
  source: 'inventory' | 'discovered' | string
  id: number
  name: string
  ip: string
  mac: string
  os: string
  type_id: number
  type_name: string
  detected: string
  kind: string
  evidence: string
  location: string
}

export type PassSafeFilters = { deviceType: number; deviceId: number; company: number }

export const passSafeShellQuery = queryOptions({
  queryKey: ['settings', 'pass_safe', 'shell'],
  queryFn: () => apiGet<PassSafeShell>('/settings/pass_safe'),
})

export type PassSafeTableQuery = {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
  filters: PassSafeFilters
}

export function passSafeTableQuery(query: PassSafeTableQuery) {
  const params = new URLSearchParams()
  params.set('draw', String(query.draw))
  params.set('start', String(query.start))
  params.set('length', String(query.length))
  params.set('order[0][column]', String(query.orderColumn))
  params.set('order[0][dir]', query.orderDir)
  params.set(`columns[${query.orderColumn}][name]`, query.orderName)
  params.set('search[value]', query.search)
  if (query.filters.deviceType > 0) params.set('deviceTypeFilter', String(query.filters.deviceType))
  if (query.filters.deviceId > 0) params.set('deviceIdFilter', String(query.filters.deviceId))
  if (query.filters.company > 0) params.set('companyFilter', String(query.filters.company))

  return queryOptions({
    queryKey: ['settings', 'pass_safe', 'list', query],
    queryFn: () => apiGet<TablePage<PassSafeRow>>(`/settings/pass_safe/list?${params.toString()}`),
  })
}

export function searchDevices(term: string, office: number) {
  return apiPost<{ devices: DeviceHit[] }>('/settings/pass_safe/search', { q: term, office })
}

/** Aparatele unui tip - al doilea select din panoul de filtre. */
export function devicesOfTypeQuery(deviceType: number) {
  return queryOptions({
    queryKey: ['settings', 'pass_safe', 'devices', deviceType],
    queryFn: () => apiGet<{ devices: Option[] }>(`/settings/pass_safe/devices/${deviceType}`),
    enabled: deviceType > 0,
  })
}

/** Ce se trimite pentru un rol completat. */
export type CredentialInput = { user: string; password: string; description: string }

export function createPasswords(payload: {
  device_id: number
  discovered_id: number
  device_type: number
  creds: Record<string, CredentialInput>
}) {
  return apiPostJson<{ created: number; failed: number; error?: string }>('/settings/pass_safe/passwords', payload)
}

export function updatePassword(id: number, fields: { user: string; new_password: string; description: string }) {
  return apiPost<{ saved: boolean }>(`/settings/pass_safe/passwords/${id}`, fields)
}

export function removePassword(id: number) {
  return apiPost<{ removed: boolean }>(`/settings/pass_safe/passwords/${id}/remove`, {})
}

/** Un singur drum, cerut anume, jurnalizat pe server. */
export function revealPassword(id: number) {
  return apiPost<{ password: string }>(`/settings/pass_safe/passwords/${id}/reveal`, {})
}

export type ImportMatchHit = {
  source: string
  id: number
  name: string
  ip: string
  mac: string
  type_id: number
  type_name: string
  kind?: string
}

export type ImportDraft = {
  key: string
  found_name: string
  ip: string
  mac: string
  type_guess: string
  kind: string
  role: string
  user: string
  password: string
  description: string
  device_type: number
  action: 'existing' | 'suggest' | 'create' | string
  match: ImportMatchHit | null
  suggestions: ImportMatchHit[]
  missing: string[]
}

export type ImportPreview = {
  items: ImportDraft[]
  offices: Option[]
  deviceTypes: Option[]
}

export function previewPassSafeImport(file: File) {
  const body = new FormData()
  body.append('file', file)
  return apiPostForm<ImportPreview>('/settings/pass_safe/import/preview', body)
}

export type ImportCommitRow = {
  include: boolean
  action: string
  found_name: string
  ip: string
  device_id: number
  discovered_id: number
  device_type: number
  office_id: number
  role: string
  user: string
  password: string
  description: string
}

export function commitPassSafeImport(items: ImportCommitRow[]) {
  return apiPostJson<{ created_devices: number; created_passwords: number; failed: number; errors?: string[] }>(
    '/settings/pass_safe/import/commit',
    { items },
  )
}

/** Coloana SQL a fiecarui indice, ALINIATA la ce vede omul - vezi pagina. */
export const PASS_SAFE_ORDER_NAMES = ['description', 'username', 'user', 'device_type', 'devices', 'company', '']
